import { t } from '@lingui/macro';
import React, { useContext, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';

import Building from '@decisiv/iconix/lib/components/Building';
import List from '@decisiv/iconix/lib/components/List';
import BarChart from '@decisiv/iconix/lib/components/BarChart';
import Home from '@decisiv/iconix/lib/components/Home';
import Truck from '@decisiv/iconix/lib/components/Truck';
import Calendar from '@decisiv/iconix/lib/components/Calendar';
import Cog from '@decisiv/iconix/lib/components/Cog';
import UsersGroup from '@decisiv/iconix/lib/components/UsersGroup';
import Folder from '@decisiv/iconix/lib/components/Folder';

import LeftNav, {
  useStickyLeftNav,
} from '@decisiv/ui-components/lib/components/LeftNav';

import TopNav from 'components/TopNav';

import ROUTE_NAMES from 'setup/Routing/routeNames';

import useSentinelTeam from 'hooks/useSentinelTeam';

import { useCurrentUserContext } from 'utils/User';
import { CurrentInvitationContext } from 'utils/Invitation';
import useFeatureFlag, { FEATURE_FLAGS } from 'utils/useFeatureFlag';

import { Outlet } from 'react-router-dom';
import FaviconManager from 'components/FaviconManager';
import ScrollingContent from './ScrollingContent';
import Versions from './Versions';

import {
  LeftNavContainer,
  NavColumnContainer,
  PageContainer,
  PrimaryContentContainer,
  TopNavContainer,
} from './styles';
import useCaseAdminUrlsQuery from './useCaseAdminUrlsQuery';

const fleetAssetGroupingItem = {
  icon: Building,
  text: t`Depots`,
  path: ROUTE_NAMES.depots,
};
const customerAssetGroupingItem = {
  icon: Building,
  text: t`Customers`,
  path: ROUTE_NAMES.customers,
};

function Layout() {
  const { authenticationKey } = useContext(CurrentInvitationContext);

  const assetViewsEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_ASSET_VIEWS);

  const variant = useMemo(
    () => (authenticationKey ? 'single-page' : 'primary'),
    [authenticationKey],
  );

  const {
    isDealerGroup,
    isFleet,
    isDealerTeam,
    oemSegment,
    belongsToOemSegment,
  } = useSentinelTeam();
  const { id, isAdmin, isSuperUser, needsSentinelTeam, availableAssetViews } =
    useCurrentUserContext();

  const [{ isExpanded, navExpandedState }, handleChangeNavExpandedState] =
    useStickyLeftNav({
      getPreferredNavExpandedState: () =>
        memoryDB.getItem('navExpandedState') ?? undefined,
      setPreferredNavExpandedState: (state) =>
        memoryDB[state ? 'setItem' : 'removeItem']('navExpandedState', state),
    });

  const canViewAdminCaseUrls =
    isAdmin && belongsToOemSegment && !needsSentinelTeam;

  const { data: adminUrlsData } = useCaseAdminUrlsQuery({
    skip: !canViewAdminCaseUrls,
  });

  const caseAdminUrls = useMemo(
    () => adminUrlsData?.currentUserProfile?.user?.caseAdminUrls || [],
    [adminUrlsData],
  );

  const caseAdminUrlsItems = useMemo(() => {
    return caseAdminUrls.map((caseUrl) => ({
      url: caseUrl.url,
      text: t`Tools for ${caseUrl.productName}`,
    }));
  }, [caseAdminUrls]);

  const assetViews = useMemo(() => {
    if (isEmpty(availableAssetViews)) return undefined;

    return availableAssetViews.map((assetView) => ({
      text: assetView.name,
      path: `/assets/${assetView.slug}`,
    }));
  }, [availableAssetViews]);

  const assetViewItems = useMemo(
    () => (assetViews && assetViewsEnabled ? { items: assetViews } : {}),
    [assetViews, assetViewsEnabled],
  );

  const partialAdminAccess = oemSegment?.settings?.partialAdminAccess ?? false;
  const hasFullAdminAccess = isSuperUser || !partialAdminAccess;
  const showInsiteReports = oemSegment?.settings?.showInsiteReports ?? false;

  const items = useMemo(() => {
    if (!id) {
      return [];
    }

    if (needsSentinelTeam) {
      return [
        isSuperUser && {
          icon: UsersGroup,
          path: ROUTE_NAMES.superadmin.teams,
          text: t`Teams`,
        },
        {
          icon: Building,
          path: ROUTE_NAMES.admin.oem_segments,
          text: t`Oem Segments`,
        },
        {
          icon: List,
          path: ROUTE_NAMES.admin.management,
          text: t`Management`,
        },
        isSuperUser && {
          icon: Folder,
          path: ROUTE_NAMES.superadmin.oem_onboard,
          text: t`OemSegments Onboarding`,
        },
        isSuperUser &&
          !showInsiteReports && {
            icon: BarChart,
            path: ROUTE_NAMES.superadmin.reports,
            text: t`Reports`,
          },
      ].filter(Boolean);
    }

    const assetGroupingItem = isFleet
      ? fleetAssetGroupingItem
      : customerAssetGroupingItem;

    const showServiceLocations =
      hasFullAdminAccess && (isDealerGroup || isDealerTeam);

    const adminSubItems = [
      ...caseAdminUrlsItems,
      hasFullAdminAccess && {
        text: t`Account`,
        path: ROUTE_NAMES.admin.account,
      },
      {
        text: t`Members`,
        path: ROUTE_NAMES.admin.members,
      },
      showServiceLocations && {
        text: t`Service Locations`,
        path: ROUTE_NAMES.admin.service_locations,
      },
      hasFullAdminAccess && {
        text: t`Integrations`,
        path: ROUTE_NAMES.admin.integrations,
      },
      {
        text: t`Preferences`,
        path: ROUTE_NAMES.admin.preferences,
      },
    ].filter(Boolean);

    const adminItems = isAdmin
      ? [
          {
            icon: Cog,
            text: t`Administration`,
            ...(hasFullAdminAccess ? { path: ROUTE_NAMES.admin.root } : {}),
            items: adminSubItems,
          },
        ]
      : [];

    return [
      {
        icon: Home,
        path: ROUTE_NAMES.dashboard,
        text: t`Home`,
      },
      {
        icon: Truck,
        path: ROUTE_NAMES.alerts,
        text: t`Assets`,
        ...assetViewItems,
      },
      {
        icon: Calendar,
        path: ROUTE_NAMES.service_events,
        text: t`Service Events`,
      },
      assetGroupingItem,
      !showInsiteReports && {
        icon: BarChart,
        path: ROUTE_NAMES.reports,
        text: t`Reports`,
      },
      ...adminItems,
    ].filter(Boolean);
  }, [
    id,
    needsSentinelTeam,
    isFleet,
    isAdmin,
    hasFullAdminAccess,
    isDealerGroup,
    isDealerTeam,
    showInsiteReports,
    assetViewItems,
    isSuperUser,
    caseAdminUrlsItems,
  ]);

  return (
    <PageContainer>
      <FaviconManager />
      {variant === 'primary' && (
        <NavColumnContainer>
          <LeftNavContainer>
            <LeftNav
              currentPath={window.location.pathname}
              items={items}
              navExpandedState={navExpandedState}
              onChangeNavExpandedState={handleChangeNavExpandedState}
            />
          </LeftNavContainer>
          {isExpanded && <Versions />}
        </NavColumnContainer>
      )}
      <PrimaryContentContainer>
        <TopNavContainer>
          <TopNav />
        </TopNavContainer>
        <ScrollingContent>
          <Outlet />
        </ScrollingContent>
      </PrimaryContentContainer>
    </PageContainer>
  );
}

export default Layout;
